import { useEffect, useRef, useState } from "react";
import { Download, FileSpreadsheet, FileJson, FileArchive, TrendingUp, CheckCircle2, Edit3, Clock, Loader2, Cloud, Rocket, XCircle, RefreshCcw, AlertCircle, Presentation } from "lucide-react";
import {
  getStats,
  downloadExportFile,
  startRoboflowPush,
  getRoboflowPushSummary,
  listRoboflowPushes,
} from "@/lib/api";
import { toast } from "sonner";

export default function ExportPage() {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [onlyReviewed, setOnlyReviewed] = useState(true);

  useEffect(() => {
    getStats()
      .then(setStats)
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  const readyForRetrain = (stats?.reviewed ?? 0) >= 100;

  return (
    <div className="p-6 sm:p-8 lg:p-10 max-w-[1400px] mx-auto">
      <div className="mb-8">
        <div className="text-[10px] uppercase tracking-[0.14em] text-zinc-500 font-semibold mb-2">
          Active Learning
        </div>
        <h1 className="font-display text-3xl sm:text-4xl font-semibold tracking-tight text-zinc-50">
          Export Koreksi untuk Retraining
        </h1>
        <p className="text-sm text-zinc-500 mt-2 max-w-3xl leading-relaxed">
          Data yang sudah diverifikasi radiolog bisa Anda download dalam format{" "}
          <strong className="text-zinc-300">COCO JSON</strong> (untuk Roboflow /
          Label Studio) atau <strong className="text-zinc-300">CSV</strong> (untuk
          analisis). Upload ke Roboflow untuk train version berikutnya.
        </p>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
        <StatCard
          label="Total Pemeriksaan"
          value={stats?.total_exams ?? 0}
          icon={TrendingUp}
          tone="indigo"
          loading={loading}
          testId="export-stat-total"
        />
        <StatCard
          label="AI Confirmed"
          value={stats?.confirmed ?? 0}
          icon={CheckCircle2}
          tone="emerald"
          loading={loading}
          testId="export-stat-confirmed"
        />
        <StatCard
          label="Radiologist Corrected"
          value={stats?.corrected ?? 0}
          icon={Edit3}
          tone="indigo"
          loading={loading}
          testId="export-stat-corrected"
        />
        <StatCard
          label="Belum Direview"
          value={stats?.unreviewed ?? 0}
          icon={Clock}
          tone="amber"
          loading={loading}
          testId="export-stat-unreviewed"
        />
      </div>

      {/* Retraining readiness */}
      <div
        className={`rounded-xl border p-5 mb-8 ${
          readyForRetrain
            ? "border-emerald-500/20 bg-emerald-500/5"
            : "border-zinc-900 bg-zinc-900/40"
        }`}
      >
        <div className="flex items-center justify-between gap-4 flex-wrap">
          <div>
            <div className="text-[10px] uppercase tracking-[0.14em] text-zinc-500 font-semibold mb-1">
              Progress ke retraining
            </div>
            <div className="font-display text-2xl font-medium">
              {stats?.reviewed ?? 0}{" "}
              <span className="text-zinc-500 text-lg">/ 100 verified</span>
            </div>
          </div>
          {readyForRetrain ? (
            <span className="text-xs px-3 py-1.5 rounded-full bg-emerald-500/10 text-emerald-400 border border-emerald-500/20">
              ✓ Siap retrain
            </span>
          ) : (
            <span className="text-xs text-zinc-500">
              Butuh {Math.max(0, 100 - (stats?.reviewed ?? 0))} verifikasi lagi
            </span>
          )}
        </div>
        <div className="mt-3 h-2 rounded-full bg-zinc-800 overflow-hidden">
          <div
            className={`h-full transition-all ${
              readyForRetrain
                ? "bg-gradient-to-r from-emerald-500 to-teal-500"
                : "bg-gradient-to-r from-indigo-500 to-violet-500"
            }`}
            style={{
              width: `${Math.min(100, ((stats?.reviewed ?? 0) / 100) * 100)}%`,
            }}
          />
        </div>
      </div>

      {/* Toggle */}
      <div className="mb-6 flex items-center gap-3">
        <label className="flex items-center gap-2 text-sm text-zinc-300 cursor-pointer select-none">
          <input
            type="checkbox"
            checked={onlyReviewed}
            onChange={(e) => setOnlyReviewed(e.target.checked)}
            data-testid="only-reviewed-toggle"
            className="w-4 h-4 rounded border-zinc-700 bg-zinc-950 text-indigo-500 focus:ring-indigo-500"
          />
          Export hanya yang sudah diverifikasi radiolog
          <span className="text-xs text-zinc-500">
            (rekomendasi untuk retraining)
          </span>
        </label>
      </div>

      {/* Download cards */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <ExportCard
          icon={FileArchive}
          title="Bundle ZIP"
          description="Paket lengkap 1-klik: COCO JSON + semua gambar X-ray dalam 1 file .zip. Tidak perlu download gambar satu-satu. Struktur folder sudah sesuai dengan file_name di COCO."
          note="✨ Rekomendasi utama — langsung drag & drop ke Roboflow Upload."
          endpoint={`/export/dataset.zip?only_reviewed=${onlyReviewed}`}
          testId="download-zip-btn"
          filename="mcu_xray_dataset.zip"
          highlight
        />
        <ExportCard
          icon={FileJson}
          title="COCO JSON"
          description="Hanya file annotations (tanpa gambar). Untuk yang sudah punya gambar terpisah atau mau merge dengan dataset lain."
          note="Format industry-standard object detection."
          endpoint={`/export/corrections.coco.json?only_reviewed=${onlyReviewed}`}
          testId="download-coco-btn"
          filename="mcu_xray_corrections.coco.json"
        />
        <ExportCard
          icon={FileSpreadsheet}
          title="CSV"
          description="Format tabular untuk analisis (Excel / pandas). Satu baris = 1 bounding box, lengkap dengan patient info + koordinat."
          note="Untuk laporan internal / statistical analysis."
          endpoint={`/export/corrections.csv?only_reviewed=${onlyReviewed}`}
          testId="download-csv-btn"
          filename="mcu_xray_corrections.csv"
        />
      </div>

      {/* Auto-Push to Roboflow */}
      <RoboflowPushPanel stats={stats} onlyReviewed={onlyReviewed} />

      {/* Marketing / Presentation Deck */}
      <MarketingDeckPanel />

      {/* Instructions */}
      <div className="mt-10 rounded-xl border border-zinc-900 bg-zinc-900/40 p-6">
        <div className="text-[10px] uppercase tracking-[0.14em] text-zinc-500 font-semibold mb-3">
          Panduan Retraining di Roboflow
        </div>
        <ol className="space-y-3 text-sm text-zinc-300">
          <Step num={1}>
            Download <strong>Bundle ZIP</strong> di atas (sudah berisi COCO JSON + semua gambar sekaligus)
          </Step>
          <Step num={2}>
            Extract ZIP-nya — akan muncul folder <code className="text-indigo-300 font-mono">images/</code> + file <code className="text-indigo-300 font-mono">annotations.coco.json</code>
          </Step>
          <Step num={3}>
            Buka Roboflow → project <code className="text-indigo-300 font-mono">mcu-2</code> → tab <strong>Upload</strong>
          </Step>
          <Step num={4}>
            Drag & drop ISI folder yang di-extract tadi (gambar + JSON). Annotation Format: <strong>COCO</strong>. Roboflow otomatis match gambar dengan anotasi via <code className="text-indigo-300 font-mono">file_name</code>.
          </Step>
          <Step num={5}>
            <strong>Generate</strong> version baru dengan augmentation yang sama seperti sebelumnya
          </Step>
          <Step num={6}>
            Klik <strong>Train</strong> — tunggu ~1-3 jam untuk RF-DETR NAS training
          </Step>
          <Step num={7}>
            Setelah selesai, klik <strong>Deploy Model</strong> pada kandidat terbaik → salin Model ID baru → update env variable <code className="text-indigo-300 font-mono">ROBOFLOW_MODEL_ID</code> di Emergent → Redeploy
          </Step>
        </ol>

        <div className="mt-6 pt-6 border-t border-zinc-800 text-xs text-zinc-500 leading-relaxed">
          <strong className="text-zinc-400">💡 Tips:</strong> Bundle ZIP sudah mencakup semuanya —
          gambar dan anotasi dijamin sinkron. Kalau perlu 1 gambar spesifik saja, tetap tersedia via
          endpoint <code className="text-indigo-300 font-mono">/api/export/images/{"{exam_id}"}</code>.
        </div>
      </div>
    </div>
  );
}

function StatCard({ label, value, icon: Icon, tone = "indigo", loading, testId }) {
  const toneCls =
    tone === "emerald"
      ? "bg-emerald-500/10 text-emerald-400"
      : tone === "amber"
      ? "bg-amber-500/10 text-amber-400"
      : "bg-indigo-500/10 text-indigo-400";
  return (
    <div
      data-testid={testId}
      className="rounded-xl border border-zinc-900 bg-zinc-900/40 p-5 hover:border-zinc-800 transition-colors"
    >
      <div className="flex items-center justify-between mb-3">
        <span className="text-[10px] uppercase tracking-[0.14em] text-zinc-500 font-semibold">
          {label}
        </span>
        <div className={`w-8 h-8 rounded-md flex items-center justify-center ${toneCls}`}>
          <Icon className="w-4 h-4" strokeWidth={1.75} />
        </div>
      </div>
      <div className="font-display text-4xl font-semibold tracking-tight text-zinc-50">
        {loading ? "..." : value}
      </div>
    </div>
  );
}

function ExportCard({ icon: Icon, title, description, note, endpoint, testId, filename, highlight = false }) {
  const [loading, setLoading] = useState(false);
  const handleDownload = async () => {
    setLoading(true);
    try {
      await downloadExportFile(endpoint, filename);
      toast.success(`${filename} downloaded`);
    } catch (e) {
      toast.error("Gagal download", {
        description: String(e?.response?.data?.detail || e.message).slice(0, 200),
      });
    } finally {
      setLoading(false);
    }
  };
  return (
    <div
      className={`rounded-xl border p-6 transition-colors flex flex-col ${
        highlight
          ? "border-indigo-500/40 bg-indigo-500/5 shadow-lg shadow-indigo-500/10 hover:border-indigo-500/60"
          : "border-zinc-900 bg-zinc-900/40 hover:border-zinc-800"
      }`}
    >
      <div
        className={`w-11 h-11 rounded-lg flex items-center justify-center mb-4 ${
          highlight ? "bg-indigo-500/20" : "bg-indigo-500/10"
        }`}
      >
        <Icon className="w-5 h-5 text-indigo-400" strokeWidth={1.75} />
      </div>
      <div className="font-display text-xl font-medium text-zinc-100 mb-2">{title}</div>
      <p className="text-sm text-zinc-400 leading-relaxed mb-3">{description}</p>
      <p className={`text-xs leading-relaxed mb-5 ${highlight ? "text-indigo-300" : "text-zinc-500"}`}>
        {note}
      </p>
      <div className="mt-auto">
        <button
          onClick={handleDownload}
          disabled={loading}
          data-testid={testId}
          className="inline-flex items-center gap-2 rounded-lg bg-indigo-600 hover:bg-indigo-500 disabled:opacity-50 text-white px-4 py-2.5 text-sm font-medium shadow-lg shadow-indigo-500/20 transition-colors"
        >
          {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Download className="w-4 h-4" />}
          Download {title}
        </button>
      </div>
    </div>
  );
}

function Step({ num, children }) {
  return (
    <li className="flex items-start gap-3">
      <span className="shrink-0 w-6 h-6 rounded-full bg-indigo-500/10 text-indigo-300 flex items-center justify-center text-xs font-mono font-semibold">
        {num}
      </span>
      <span className="pt-0.5">{children}</span>
    </li>
  );
}

function RoboflowPushPanel({ stats, onlyReviewed }) {
  const [activeJob, setActiveJob] = useState(null); // job summary
  const [loading, setLoading] = useState(false);
  const [starting, setStarting] = useState(false);
  const [expanded, setExpanded] = useState(false);
  const [batchName, setBatchName] = useState("");
  const [split, setSplit] = useState("train");
  const [onlyUnpushed, setOnlyUnpushed] = useState(true);
  const [history, setHistory] = useState([]);
  const pollRef = useRef(null);

  const reviewedCount = stats?.reviewed ?? 0;

  const refreshHistory = async () => {
    try {
      const list = await listRoboflowPushes();
      setHistory(list);
      const running = list.find((j) => j.status === "processing");
      if (running) setActiveJob(running);
      else if (!activeJob) setActiveJob(list[0] || null);
    } catch (e) {
      // silent
    }
  };

  useEffect(() => {
    refreshHistory().finally(() => setLoading(false));
    return () => {
      if (pollRef.current) clearInterval(pollRef.current);
    };
  }, []);

  // Poll active job while processing
  useEffect(() => {
    if (!activeJob || activeJob.status !== "processing") {
      if (pollRef.current) clearInterval(pollRef.current);
      return;
    }
    pollRef.current = setInterval(async () => {
      try {
        const s = await getRoboflowPushSummary(activeJob.id);
        setActiveJob(s);
        if (s.status !== "processing") {
          clearInterval(pollRef.current);
          refreshHistory();
        }
      } catch (e) {
        clearInterval(pollRef.current);
      }
    }, 2000);
    return () => pollRef.current && clearInterval(pollRef.current);
  }, [activeJob?.id, activeJob?.status]);

  const startPush = async () => {
    setStarting(true);
    try {
      const job = await startRoboflowPush({
        only_reviewed: onlyReviewed,
        only_unpushed: onlyUnpushed,
        batch_name: batchName.trim(),
        split,
      });
      // Convert to summary-shape
      setActiveJob({ ...job, done: 0, failed: 0, skipped: 0, total: job.items_count });
      toast.success(`Push dimulai: ${job.items_count} exam ke Roboflow project ${job.project_slug}`);
    } catch (e) {
      toast.error("Gagal memulai push", {
        description: String(e?.response?.data?.detail || e.message).slice(0, 260),
      });
    } finally {
      setStarting(false);
    }
  };

  const isRunning = activeJob?.status === "processing";
  const canStart = reviewedCount > 0 && !isRunning;
  const progressPct = activeJob?.total
    ? Math.round(((activeJob.done + activeJob.failed + activeJob.skipped) / activeJob.total) * 100)
    : 0;

  return (
    <div
      className="mt-8 rounded-xl border border-indigo-500/30 bg-gradient-to-br from-indigo-500/10 via-violet-500/5 to-zinc-900/40 p-6"
      data-testid="roboflow-push-panel"
    >
      <div className="flex items-start justify-between gap-4 flex-wrap mb-4">
        <div className="flex items-start gap-3">
          <div className="w-11 h-11 rounded-lg bg-indigo-500/20 flex items-center justify-center shrink-0">
            <Rocket className="w-5 h-5 text-indigo-300" strokeWidth={1.75} />
          </div>
          <div>
            <div className="text-[10px] uppercase tracking-[0.14em] text-indigo-300 font-semibold mb-0.5">
              1-Click Retraining
            </div>
            <h3 className="font-display text-xl font-medium text-zinc-50">
              Auto-Push ke Roboflow
            </h3>
            <p className="text-sm text-zinc-400 leading-relaxed mt-1 max-w-2xl">
              Kirim langsung semua koreksi yang sudah diverifikasi ke Roboflow project{" "}
              <code className="text-indigo-300 font-mono bg-indigo-500/10 px-1.5 py-0.5 rounded text-xs">
                mcu-2
              </code>{" "}
              — tanpa download-upload manual. Gambar + anotasi (YOLO format dengan labelmap) akan otomatis di-attach.
            </p>
          </div>
        </div>
        <button
          onClick={() => setExpanded((v) => !v)}
          data-testid="toggle-push-options-btn"
          className="text-xs text-zinc-400 hover:text-zinc-200 underline"
        >
          {expanded ? "Sembunyikan opsi" : "Opsi lanjutan"}
        </button>
      </div>

      {expanded && (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-4 p-4 rounded-lg bg-zinc-950/60 border border-zinc-800">
          <div>
            <label className="text-[10px] uppercase tracking-[0.14em] text-zinc-500 font-semibold block mb-1.5">
              Batch Name (optional)
            </label>
            <input
              type="text"
              value={batchName}
              onChange={(e) => setBatchName(e.target.value)}
              placeholder="thorax-ai-YYYYMMDD"
              data-testid="push-batch-name-input"
              className="block w-full rounded-lg border border-zinc-800 bg-zinc-950 px-3 py-2 text-sm text-zinc-100 placeholder:text-zinc-600 focus:border-indigo-500 outline-none"
            />
          </div>
          <div>
            <label className="text-[10px] uppercase tracking-[0.14em] text-zinc-500 font-semibold block mb-1.5">
              Split
            </label>
            <select
              value={split}
              onChange={(e) => setSplit(e.target.value)}
              data-testid="push-split-select"
              className="block w-full rounded-lg border border-zinc-800 bg-zinc-950 px-3 py-2 text-sm text-zinc-100 focus:border-indigo-500 outline-none"
            >
              <option value="train">Train</option>
              <option value="valid">Validation</option>
              <option value="test">Test</option>
            </select>
          </div>
          <div className="flex items-end">
            <label className="flex items-center gap-2 text-sm text-zinc-300 cursor-pointer select-none">
              <input
                type="checkbox"
                checked={onlyUnpushed}
                onChange={(e) => setOnlyUnpushed(e.target.checked)}
                data-testid="push-only-unpushed"
                className="w-4 h-4 rounded border-zinc-700 bg-zinc-950 text-indigo-500"
              />
              Hanya yang belum pernah di-push
            </label>
          </div>
        </div>
      )}

      <div className="flex items-center gap-3 flex-wrap">
        <button
          onClick={startPush}
          disabled={!canStart || starting}
          data-testid="start-roboflow-push-btn"
          className="inline-flex items-center gap-2 rounded-lg bg-indigo-600 hover:bg-indigo-500 disabled:opacity-40 disabled:cursor-not-allowed text-white px-5 py-2.5 text-sm font-semibold shadow-lg shadow-indigo-500/30 transition-colors"
        >
          {starting || isRunning ? (
            <Loader2 className="w-4 h-4 animate-spin" />
          ) : (
            <Cloud className="w-4 h-4" />
          )}
          {isRunning
            ? "Sedang memproses..."
            : starting
            ? "Memulai..."
            : `Push ${reviewedCount} verified exam ke Roboflow`}
        </button>
        <button
          onClick={refreshHistory}
          data-testid="refresh-push-history-btn"
          className="inline-flex items-center gap-1.5 rounded-lg border border-zinc-800 bg-zinc-950 hover:border-zinc-700 text-zinc-300 px-3 py-2 text-xs font-medium"
        >
          <RefreshCcw className="w-3.5 h-3.5" /> Refresh
        </button>
      </div>

      {/* Active/Latest job progress */}
      {activeJob && (
        <div
          className="mt-5 rounded-lg border border-zinc-800 bg-zinc-950/60 p-4"
          data-testid="active-push-progress"
        >
          <div className="flex items-center justify-between gap-3 mb-3 flex-wrap">
            <div>
              <div className="text-xs text-zinc-400">
                Batch: <span className="text-zinc-200 font-mono">{activeJob.batch_name}</span>
              </div>
              <div className="text-[10px] font-mono text-zinc-500 mt-0.5">
                Started {new Date(activeJob.created_at).toLocaleString()}
                {activeJob.completed_at && ` · Completed ${new Date(activeJob.completed_at).toLocaleString()}`}
              </div>
            </div>
            <StatusBadge status={activeJob.status} />
          </div>

          <div className="h-2 rounded-full bg-zinc-900 overflow-hidden mb-3">
            <div
              className={`h-full transition-all ${
                activeJob.status === "failed"
                  ? "bg-rose-500"
                  : activeJob.failed > 0
                  ? "bg-amber-500"
                  : "bg-gradient-to-r from-indigo-500 to-violet-500"
              }`}
              style={{ width: `${progressPct}%` }}
            />
          </div>

          <div className="grid grid-cols-4 gap-2 text-center">
            <MiniStat label="Total" value={activeJob.total || 0} />
            <MiniStat label="Selesai" value={activeJob.done || 0} tone="emerald" />
            <MiniStat label="Skip (duplikat)" value={activeJob.skipped || 0} tone="zinc" />
            <MiniStat label="Gagal" value={activeJob.failed || 0} tone="rose" />
          </div>

          {activeJob.status === "failed" && activeJob.error && (
            <div className="mt-3 rounded-lg border border-rose-500/30 bg-rose-500/10 p-3 flex items-start gap-2 text-xs">
              <AlertCircle className="w-4 h-4 text-rose-400 mt-0.5 shrink-0" />
              <div className="text-rose-300 font-mono">{activeJob.error}</div>
            </div>
          )}
        </div>
      )}

      {/* History (last 5) */}
      {history.length > 1 && (
        <details className="mt-4">
          <summary className="text-xs text-zinc-400 hover:text-zinc-200 cursor-pointer font-medium">
            Riwayat push ({history.length})
          </summary>
          <div className="mt-2 space-y-1.5">
            {history.slice(0, 10).map((j) => (
              <button
                key={j.id}
                onClick={() => setActiveJob(j)}
                className="w-full flex items-center justify-between gap-3 rounded-lg border border-zinc-900 hover:border-zinc-800 bg-zinc-950/50 px-3 py-2 text-xs text-left"
              >
                <div>
                  <div className="text-zinc-200 font-mono">{j.batch_name}</div>
                  <div className="text-zinc-500 text-[10px]">
                    {new Date(j.created_at).toLocaleString()} · {j.total} exams
                  </div>
                </div>
                <StatusBadge status={j.status} compact />
              </button>
            ))}
          </div>
        </details>
      )}
    </div>
  );
}

function StatusBadge({ status, compact = false }) {
  const map = {
    processing: { label: "Processing", cls: "bg-indigo-500/10 text-indigo-300 border-indigo-500/30", Icon: Loader2, spin: true },
    done: { label: "Selesai", cls: "bg-emerald-500/10 text-emerald-300 border-emerald-500/30", Icon: CheckCircle2 },
    done_with_errors: { label: "Selesai (sebagian gagal)", cls: "bg-amber-500/10 text-amber-300 border-amber-500/30", Icon: AlertCircle },
    failed: { label: "Failed", cls: "bg-rose-500/10 text-rose-300 border-rose-500/30", Icon: XCircle },
  };
  const s = map[status] || map.processing;
  const Icon = s.Icon;
  return (
    <span className={`inline-flex items-center gap-1.5 rounded-full border ${s.cls} ${compact ? "px-2 py-0.5 text-[10px]" : "px-2.5 py-1 text-xs"} font-medium`}>
      <Icon className={`${compact ? "w-3 h-3" : "w-3.5 h-3.5"} ${s.spin ? "animate-spin" : ""}`} />
      {s.label}
    </span>
  );
}

function MiniStat({ label, value, tone = "indigo" }) {
  const toneCls =
    tone === "emerald"
      ? "text-emerald-300"
      : tone === "rose"
      ? "text-rose-300"
      : tone === "zinc"
      ? "text-zinc-300"
      : "text-indigo-300";
  return (
    <div className="rounded-md bg-zinc-900/60 border border-zinc-800 py-2 px-2">
      <div className={`text-lg font-display font-semibold ${toneCls}`}>{value}</div>
      <div className="text-[9px] uppercase tracking-wider text-zinc-500 font-semibold">{label}</div>
    </div>
  );
}

function MarketingDeckPanel() {
  const [loading, setLoading] = useState(false);
  const doDownload = async () => {
    setLoading(true);
    try {
      await downloadExportFile("/marketing/presentation.pdf", "ThoraxAI_Presentation.pdf");
      toast.success("Slide deck di-download");
    } catch (e) {
      toast.error("Gagal download deck");
    } finally {
      setLoading(false);
    }
  };
  return (
    <div
      className="mt-8 rounded-xl border border-violet-500/30 bg-gradient-to-br from-violet-500/10 via-indigo-500/5 to-zinc-900/40 p-6"
      data-testid="marketing-deck-panel"
    >
      <div className="flex items-start gap-4 flex-wrap justify-between">
        <div className="flex items-start gap-3 min-w-0">
          <div className="w-11 h-11 rounded-lg bg-violet-500/20 flex items-center justify-center shrink-0">
            <Presentation className="w-5 h-5 text-violet-300" strokeWidth={1.75} />
          </div>
          <div className="min-w-0">
            <div className="text-[10px] uppercase tracking-[0.14em] text-violet-300 font-semibold mb-0.5">
              Sales & Technical Deck
            </div>
            <h3 className="font-display text-xl font-medium text-zinc-50">
              Slide Presentasi (PDF · 16 halaman)
            </h3>
            <p className="text-sm text-zinc-400 leading-relaxed mt-1 max-w-2xl">
              Deck lengkap untuk presentasi ke Direktur RS / IT / Radiolog: masalah,
              solusi, alur kerja, kelas deteksi, arsitektur, security, roadmap, dan CTA.
              Auto-includes statistik live (total exams, users, verified) dari database Anda.
            </p>
          </div>
        </div>
        <button
          onClick={doDownload}
          disabled={loading}
          data-testid="download-presentation-btn"
          className="inline-flex items-center gap-2 rounded-lg bg-violet-600 hover:bg-violet-500 disabled:opacity-50 text-white px-5 py-2.5 text-sm font-semibold shadow-lg shadow-violet-500/30 transition-colors"
        >
          {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Download className="w-4 h-4" />}
          Download Presentation PDF
        </button>
      </div>
      <div className="mt-4 grid grid-cols-2 sm:grid-cols-4 gap-2 text-center">
        <MiniStat label="Slides" value={16} tone="indigo" />
        <MiniStat label="Format" value="16:9" tone="zinc" />
        <MiniStat label="Live stats" value="✓" tone="emerald" />
        <MiniStat label="Multi-audience" value="✓" tone="emerald" />
      </div>
    </div>
  );
}
